import { UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { acceptTripInvite, deleteTripInvite, editCollaborator, editTripInvite, getTripCollaborators, removeCollaborator, sendTripInvite } from 'tripPlanner/api'
import { GetTripCollaboratorsResponse, GetTripCollaboratorError, SendInviteError, AcceptInviteError, RemoveCollaboratorError, RevokeInviteError, EditInviteError, EditCollaboratorError } from 'tripPlanner/api/types'
import * as TripKeys from '../reactQueryKeys/trips'
import { cancelAndInvalidate, removeTripQueries, TRIP_STALE_TIME } from './common'
import { Collaborator, FullTrip, GuestRole, Invitation } from 'tripPlanner/types/common'
import * as Mapper from 'tripPlanner/mappers'
import { useAppSelector } from 'hooks/reduxHooks'

type TripCollaboratorReturn = {
  collaborators: Array<Collaborator>
  invitations: Array<Invitation>
}

export const useTripCollaborators = ({
  tripId, options,
}: {
  tripId: string
  options?: Omit<
    UseQueryOptions<
      GetTripCollaboratorsResponse['result'], GetTripCollaboratorError, TripCollaboratorReturn
    >, 'queryFn'
  >
}): UseQueryResult<TripCollaboratorReturn, GetTripCollaboratorError> => {
  return useQuery({
    queryKey: TripKeys.collaborator(tripId),
    queryFn: () => getTripCollaborators(tripId),
    staleTime: TRIP_STALE_TIME,
    enabled: !!tripId,
    ...options,
  })
}

type SendInviteArgs = {
  tripId: string
  email: string
  role: GuestRole
}

export const useSendInvite = (
  tripId: string,
  options?: UseMutationOptions<null, SendInviteError, SendInviteArgs>,
): UseMutationResult<null, SendInviteError, SendInviteArgs> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, role }) => sendTripInvite({ tripId, email, role }),
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useAcceptInvite = (
  tripId: string,
  inviteId: string,
  options?: UseMutationOptions<FullTrip, AcceptInviteError, void>,
): UseMutationResult<FullTrip, AcceptInviteError, void> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => acceptTripInvite({ tripId, inviteId }).then(Mapper.fullTrip),
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.all)
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

type RemoveCollaboratorArgs = {
  userId: string
}

export const useRemoveCollaborator = (
  tripId: string,
  options?: UseMutationOptions<
    null, RemoveCollaboratorError, RemoveCollaboratorArgs
  >,
): UseMutationResult<null, RemoveCollaboratorError, RemoveCollaboratorArgs> => {
  const queryClient = useQueryClient()

  const userId = useAppSelector(
    (state: App.State) => state.auth.account.memberId,
  )

  return useMutation({
    mutationFn: ({ userId }) => removeCollaborator({ userId, tripId }),
    ...options,
    onSuccess: (data, variables, context) => {
      if (variables.userId === userId) {
        // Removed self - user no longer has access to trip
        removeTripQueries(queryClient, tripId)
      }
      options?.onSuccess?.(data, variables, context)
    },
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

type RevokeInviteArgs = {
  invitationId: string
}

export const useRevokeInvite = (
  tripId: string,
  options?: UseMutationOptions<null, RevokeInviteError, RevokeInviteArgs>,
): UseMutationResult<null, RevokeInviteError, RevokeInviteArgs> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invitationId }) => deleteTripInvite({ invitationId, tripId }),
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

type EditInviteArgs = {
  invitationId: string
  role: GuestRole
}

export const useEditInvite = (
  tripId: string,
  options?: UseMutationOptions<null, EditInviteError, EditInviteArgs>,
): UseMutationResult<null, EditInviteError, EditInviteArgs> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invitationId, role }) => editTripInvite({ invitationId, tripId, role }),
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

type EditCollaboratorArgs = {
  userId: string
  role: GuestRole
}

export const useEditCollaborator = (
  tripId: string,
  options?: UseMutationOptions<
    null, EditCollaboratorError, EditCollaboratorArgs
  >,
): UseMutationResult<null, EditCollaboratorError, EditCollaboratorArgs> => {
  const queryClient = useQueryClient()

  const userId = useAppSelector((state) => state.auth.account.memberId)

  return useMutation({
    mutationFn: ({ userId, role }) => editCollaborator({ userId, tripId, role }),
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(queryClient, TripKeys.collaborator(tripId))
      if (variables.userId === userId) {
        // If user has edited themselves, invalidate the trip so we get their current role
        cancelAndInvalidate(queryClient, TripKeys.all)
        cancelAndInvalidate(queryClient, TripKeys.detail(tripId))
      }
      options?.onSettled?.(data, error, variables, context)
    },
  })
}
